import {
  AutoAssessment,
  AutoEvaluation,
  JobDescription,
  JobOpeningList,
  JobOpeningRequest,
  JobPostingTabData,
  JobRequisition,
  jobOpeningTypes,
} from '../types/JobOpenings';
import { EmployeeUser, JobTitle } from '../types/admin_types';
import { rtkApi } from './rtkApi';

export const jobOpeningsApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getJobOpeningList: builder.query<
      { jobOpenings: JobOpeningList[]; totalJobOpenings: number },
      { body: JobOpeningRequest; page?: number; limit?: number }
    >({
      query: ({ body, page = 1, limit = 10 }) => ({
        url: `/job-openings/view?page=${page}&limit=${limit}`,
        method: 'POST',
        body,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.jobOpenings.map(({ id }) => ({ type: 'JobOpening' as const, id })),
              { type: 'JobOpening' as const, id: 'LIST' },
            ]
          : [{ type: 'JobOpening' as const, id: 'LIST' }],
    }),
    getJobOpeningDetails: builder.query<any, string>({
      query: (id) => `/job-openings/${id}/details`,
      providesTags: (result, error, id) => [{ type: 'JobOpening', id }],
    }),
    getJobApplicants: builder.query<any, { id: string; search?: string; page?: number; limit?: number; status?: string }>({
      query: ({ id, search = '', page = 1, limit = 10, status = '' }) =>
        `/job-openings/${id}/applicants?search=${search}&page=${page}&limit=${limit}&status=${status}`,
      providesTags: (result, error, { id }) => [{ type: 'JobOpening', id }],
    }),
    createJobOpening: builder.mutation<{ id: string }, string[]>({
      query: (jobOpeningElements) => ({
        url: '/job-openings',
        method: 'POST',
        body: { jobOpeningElements },
      }),
      invalidatesTags: [{ type: 'JobOpening' as const, id: 'LIST' }],
    }),
    editJobOpening: builder.mutation<any, { id: string; elements: jobOpeningTypes[] }>({
      query: ({ id, elements }) => ({
        url: `/job-openings/${id}`,
        method: 'PUT',
        body: { jobOpeningElements: elements },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'JobOpening', id }],
    }),
    createJobRequisition: builder.mutation<any, JobRequisition>({
      query: (body) => ({
        url: '/job-openings/job-requisitions',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [{ type: 'JobOpening', id: jobOpeningId }],
    }),
    createJobDescription: builder.mutation<any, JobDescription>({
      query: (body) => ({
        url: '/job-openings/job-descriptions',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [{ type: 'JobOpening', id: jobOpeningId }],
    }),
    createAutoSuitability: builder.mutation<any, AutoEvaluation>({
      query: (body) => ({
        url: '/job-openings/auto-suitability',
        method: 'POST',
        body: { ...body, id: undefined },
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [{ type: 'JobOpening', id: jobOpeningId }],
    }),
    createAutoOutstanding: builder.mutation<any, AutoEvaluation>({
      query: (body) => ({
        url: '/job-openings/auto-outstanding',
        method: 'POST',
        body: { ...body, id: undefined },
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [{ type: 'JobOpening', id: jobOpeningId }],
    }),
    createAutoAssessment: builder.mutation<any, AutoAssessment>({
      query: (body) => ({
        url: '/job-openings/auto-assessments',
        method: 'POST',
        body: { ...body, id: undefined },
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [{ type: 'JobOpening', id: jobOpeningId }],
    }),
    updateJobPosting: builder.mutation<any, JobPostingTabData>({
      query: (body) => ({
        url: '/job-openings/job-posting', // Assuming the endpoint from context
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [{ type: 'JobOpening', id: jobOpeningId }],
    }),
    getEmployees: builder.query<
      { employee: EmployeeUser[]; totalEmployees?: number },
      { searchTerm?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        const searchTerm = params?.searchTerm || '';
        const page = params?.page || 1;
        const limit = params?.limit || 100;
        return searchTerm
          ? `/employee?name=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`
          : `/employee?page=${page}&limit=${limit}`;
      },
    }),
    searchJobTitles: builder.query<JobTitle[], string | void>({
      query: (keyword) => `/job_title/search?keyword=${encodeURIComponent(keyword || '')}`,
    }),
    getAutoAssessment: builder.query<AutoAssessment, string>({
      query: (id) => `/job-openings/${id}/auto-assessments`,
      providesTags: (result, error, id) => [{ type: 'JobOpening', id }],
    }),
  }),
});

export const {
  useGetJobOpeningListQuery,
  useLazyGetJobOpeningListQuery,
  useGetJobOpeningDetailsQuery,
  useGetJobApplicantsQuery,
  useCreateJobOpeningMutation,
  useEditJobOpeningMutation,
  useCreateJobRequisitionMutation,
  useCreateJobDescriptionMutation,
  useCreateAutoSuitabilityMutation,
  useCreateAutoOutstandingMutation,
  useCreateAutoAssessmentMutation,
  useUpdateJobPostingMutation,
  useGetEmployeesQuery,
  useLazyGetEmployeesQuery,
  useSearchJobTitlesQuery,
  useLazySearchJobTitlesQuery,
  useGetAutoAssessmentQuery,
} = jobOpeningsApi;
